import type { CSSProperties } from 'react'

/**
 * Parses an inline CSS string into a React style object.
 *
 * The design export writes styles as plain CSS strings. Keeping them as CSS in
 * the generated screens means an edit here maps 1:1 onto the design doc, so the
 * two stay diffable. Results are memoised — the same literal string is parsed
 * once for the lifetime of the page.
 */
const cache = new Map<string, CSSProperties>()

export function sx(css: string): CSSProperties {
  const hit = cache.get(css)
  if (hit) return hit

  const out: Record<string, string> = {}
  for (const decl of splitDeclarations(css)) {
    const colon = decl.indexOf(':')
    if (colon === -1) continue
    const prop = decl.slice(0, colon).trim()
    const value = decl.slice(colon + 1).trim()
    if (!prop || !value) continue
    out[prop.startsWith('--') ? prop : camelise(prop)] = value
  }

  const style = out as CSSProperties
  cache.set(css, style)
  return style
}

/** Splits on `;` at paren depth 0, so `url(a;b)` and gradients survive intact. */
function splitDeclarations(css: string): string[] {
  const parts: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < css.length; i++) {
    const c = css[i]
    if (c === '(') depth++
    else if (c === ')') depth--
    else if (c === ';' && depth === 0) {
      parts.push(css.slice(start, i))
      start = i + 1
    }
  }
  parts.push(css.slice(start))
  return parts
}

/** `background-color` -> `backgroundColor`, `-webkit-mask` -> `WebkitMask`. */
function camelise(prop: string): string {
  const vendor = prop.startsWith('-')
  const camel = prop.replace(/-([a-z])/g, (_, ch: string) => ch.toUpperCase()).replace(/^-/, '')
  return vendor ? camel.charAt(0).toUpperCase() + camel.slice(1) : camel
}
